import { useEffect, useMemo } from "react";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import { prefixer } from "stylis";
import rtlPlugin from "stylis-plugin-rtl";
import { ThemeProvider } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { RTL_LANGUAGES, SelectLanguage } from "@app/constants";
import { createAppTheme } from "@app/theme/muiTheme";

type MuiDirectionProviderProps = {
  children: React.ReactNode;
};

const getDirection = (language: string) => {
  const lang = language.split("-")[0] as SelectLanguage;
  return RTL_LANGUAGES.includes(lang) ? "rtl" : "ltr";
};

const createEmotionCache = (direction: "rtl" | "ltr") =>
  createCache({
    key: direction === "rtl" ? "muirtl" : "muiltr",
    stylisPlugins: direction === "rtl" ? [prefixer, rtlPlugin] : [prefixer],
  });

export function MuiDirectionProvider({ children }: MuiDirectionProviderProps) {
  const { i18n } = useTranslation();
  const direction = getDirection(i18n.language);

  const theme = useMemo(() => createAppTheme(direction), [direction]);
  const cache = useMemo(() => createEmotionCache(direction), [direction]);

  useEffect(() => {
    document.documentElement.dir = direction;
    document.documentElement.lang = i18n.language;
  }, [direction, i18n.language]);

  return (
    <CacheProvider value={cache}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </CacheProvider>
  );
}
